"use client";

import { useState, useEffect, useCallback } from "react";

type RiskLevel = "low" | "medium" | "high" | "critical";

interface StorageEntry {
  key: string;
  value: string;
  source: "localStorage" | "sessionStorage" | "cookie";
  sizeBytes: number;
}

interface RiskFinding {
  key: string;
  source: string;
  risk: RiskLevel;
  reason: string;
  gdprArticle: string;
}

interface AuditReport {
  entries: StorageEntry[];
  findings: RiskFinding[];
  score: number; // 0-100, higher = more compliant
  summary: string;
  timestamp: string;
}

const RISK_COLOR: Record<RiskLevel, string> = {
  low: "text-green-600 bg-green-50 border-green-200",
  medium: "text-yellow-700 bg-yellow-50 border-yellow-200",
  high: "text-orange-600 bg-orange-50 border-orange-200",
  critical: "text-red-600 bg-red-50 border-red-200",
};

const RISK_BADGE: Record<RiskLevel, string> = {
  low: "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const RISK_LABEL: Record<RiskLevel, string> = {
  low: "Lav risiko",
  medium: "Medium risiko",
  high: "Høj risiko",
  critical: "Kritisk",
};

function truncate(s: string, max = 60): string {
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function scanBrowser(): AuditReport {
  const entries: StorageEntry[] = [];
  const findings: RiskFinding[] = [];

  // --- localStorage ---
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) ?? "";
      const value = localStorage.getItem(key) ?? "";
      entries.push({ key, value, source: "localStorage", sizeBytes: key.length + value.length });
    }
  } catch { /* sandboxed */ }

  // --- sessionStorage ---
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i) ?? "";
      const value = sessionStorage.getItem(key) ?? "";
      entries.push({ key, value, source: "sessionStorage", sizeBytes: key.length + value.length });
    }
  } catch { /* sandboxed */ }

  // --- document.cookie ---
  try {
    if (document.cookie) {
      document.cookie.split(";").forEach((pair) => {
        const eqIdx = pair.indexOf("=");
        const key = eqIdx >= 0 ? pair.slice(0, eqIdx).trim() : pair.trim();
        const value = eqIdx >= 0 ? pair.slice(eqIdx + 1).trim() : "";
        if (key) entries.push({ key, value, source: "cookie", sizeBytes: key.length + value.length });
      });
    }
  } catch { /* sandboxed */ }

  // --- AI-powered rule engine ---
  const EMAIL_RE = /\b[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}\b/i;
  const CPR_RE = /\b\d{6}[-–]\d{4}\b/;
  const PHONE_RE = /\b(\+45|0045)?[\s\-]?(\d{2}[\s\-]?){4}\b/;
  const JWT_RE = /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/;

  for (const e of entries) {
    const lk = e.key.toLowerCase();
    const lv = e.value.toLowerCase();
    const valRaw = e.value;

    if (CPR_RE.test(valRaw)) {
      findings.push({
        key: e.key, source: e.source, risk: "critical",
        reason: "Muligt CPR-nummer fundet i værdien — særlig kategori personoplysning.",
        gdprArticle: "GDPR art. 9 · Forordning (EU) 2016/679",
      });
    } else if (EMAIL_RE.test(valRaw)) {
      findings.push({
        key: e.key, source: e.source, risk: "critical",
        reason: "E-mailadresse registreret i browserlager — PII-eksponering.",
        gdprArticle: "GDPR art. 5.1(c) — Dataminimering",
      });
    } else if (PHONE_RE.test(valRaw)) {
      findings.push({
        key: e.key, source: e.source, risk: "high",
        reason: "Telefonnummer-mønster fundet — mulig personhenførbar information.",
        gdprArticle: "GDPR art. 5.1(c) — Dataminimering",
      });
    } else if (JWT_RE.test(valRaw) || lk.includes("token") || lk.includes("jwt") || lk.includes("bearer")) {
      findings.push({
        key: e.key, source: e.source, risk: "medium",
        reason: "Autentifikationstoken i browserlager. Brug HttpOnly-cookie i stedet for XSS-beskyttelse.",
        gdprArticle: "GDPR art. 32 — Teknisk sikkerhed",
      });
    } else if (
      lk.startsWith("_ga") || lk.startsWith("_gid") || lk.startsWith("_gat") ||
      lk.includes("analytics") || lk.includes("gtag") || lk.includes("fbp") ||
      lk.includes("_fbq") || lk.includes("pixel") || lk.includes("clarity") ||
      lk.includes("hotjar") || lk.includes("intercom") || lk.includes("hubspot")
    ) {
      findings.push({
        key: e.key, source: e.source, risk: "high",
        reason: "Tredjepartssporing/analytics-cookie uden dokumenteret forudgående samtykke.",
        gdprArticle: "GDPR art. 6.1(a) — Samtykke krævet · ePrivacy-direktivet art. 5(3)",
      });
    } else if (lk.includes("consent") || lk.includes("gdpr") || lk.includes("cookie_policy") || lk.includes("cookie-policy")) {
      findings.push({
        key: e.key, source: e.source, risk: "low",
        reason: "Samtykkedokumentation — lovpligtigt og compliant.",
        gdprArticle: "GDPR art. 7 — Samtykkebetingelser",
      });
    } else if (lk.includes("session") || lk.includes("sid") || lk.includes("auth")) {
      findings.push({
        key: e.key, source: e.source, risk: "medium",
        reason: "Sessions-ID til autentifikation. Verificer at det udløber korrekt.",
        gdprArticle: "GDPR art. 6.1(b) — Kontraktmæssig nødvendighed",
      });
    } else if (e.source === "localStorage" && e.sizeBytes > 2048) {
      findings.push({
        key: e.key, source: e.source, risk: "medium",
        reason: `Stor datamængde (${e.sizeBytes} B) i persistent lager — dokumentér opbevaringsperiode.`,
        gdprArticle: "GDPR art. 5.1(e) — Opbevaringsbegrænsning",
      });
    } else if (lv.includes("password") || lv.includes("passwor") || lk.includes("password") || lk.includes("pwd")) {
      findings.push({
        key: e.key, source: e.source, risk: "critical",
        reason: "Adgangskode-relateret nøgle eller værdi — må aldrig gemmes i browserlager.",
        gdprArticle: "GDPR art. 32 — Teknisk sikkerhed",
      });
    } else {
      findings.push({
        key: e.key, source: e.source, risk: "low",
        reason: "Funktionelt data — tilsyneladende compliant.",
        gdprArticle: "GDPR art. 6.1(f) — Legitim interesse",
      });
    }
  }

  if (entries.length === 0) {
    findings.push({
      key: "(ingen data)", source: "—", risk: "low",
      reason: "Ingen data fundet i browser-lager. Minimal dataaftryk.",
      gdprArticle: "GDPR art. 5.1(c) — Dataminimering ✅",
    });
  }

  // Score
  const weights: Record<RiskLevel, number> = { low: 0, medium: 10, high: 25, critical: 50 };
  const penalty = findings.reduce((acc, f) => acc + weights[f.risk], 0);
  const score = Math.max(0, 100 - penalty);

  const criticals = findings.filter((f) => f.risk === "critical").length;
  const highs = findings.filter((f) => f.risk === "high").length;
  const mediums = findings.filter((f) => f.risk === "medium").length;

  let summary = "";
  if (score >= 90) summary = "Fremragende overholdelse. Browser-lageret fremstår GDPR-compliant.";
  else if (score >= 70) summary = `Acceptabel status. ${mediums} medium-fund kræver opmærksomhed.`;
  else if (score >= 50) summary = `Moderat risiko. ${highs} høj-risiko-fund bør adresseres.`;
  else summary = `Kritisk: ${criticals} kritiske + ${highs} høj-risiko-fund — kræver øjeblikkelig handling.`;

  return { entries, findings, score, summary, timestamp: new Date().toLocaleString("da-DK") };
}

interface Props {
  /** Pass a callback so Footer can trigger re-open */
  onRegisterOpen?: (openFn: () => void) => void;
}

export default function CookieComplianceTester({ onRegisterOpen }: Props) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<AuditReport | null>(null);
  const [activeTab, setActiveTab] = useState<"findings" | "raw">("findings");

  const runAudit = useCallback(() => {
    setReport(scanBrowser());
    setActiveTab("findings");
  }, []);

  const openModal = useCallback(() => {
    runAudit();
    setOpen(true);
  }, [runAudit]);

  // Register open function for footer trigger
  useEffect(() => {
    onRegisterOpen?.(openModal);
  }, [onRegisterOpen, openModal]);

  // Show automatically on first page load per session
  useEffect(() => {
    const seen = sessionStorage.getItem("cookie_audit_shown");
    if (!seen) {
      sessionStorage.setItem("cookie_audit_shown", "1");
      // Small delay so page has time to render first
      const t = setTimeout(() => openModal(), 800);
      return () => clearTimeout(t);
    }
  }, [openModal]);

  if (!open || !report) return null;

  const scoreColor =
    report.score >= 90 ? "text-green-600" :
    report.score >= 70 ? "text-yellow-600" :
    report.score >= 50 ? "text-orange-600" : "text-red-600";

  const scoreBg =
    report.score >= 90 ? "bg-green-500" :
    report.score >= 70 ? "bg-yellow-500" :
    report.score >= 50 ? "bg-orange-500" : "bg-red-500";

  const grouped: Record<RiskLevel, RiskFinding[]> = { critical: [], high: [], medium: [], low: [] };
  for (const f of report.findings) grouped[f.risk].push(f);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100"
          style={{ background: "linear-gradient(135deg, #1a3d2e, #2d6a4f)" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center text-white text-lg">🔍</div>
            <div>
              <h2 className="font-bold text-white text-base leading-tight">AI Cookie Compliance Tester</h2>
              <p className="text-green-200 text-xs">GDPR · ePrivacy · Browser-lager analyse</p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg transition-colors"
            aria-label="Luk"
          >×</button>
        </div>

        {/* Score bar */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Compliance-score</span>
            <span className={`text-2xl font-bold ${scoreColor}`}>{report.score}<span className="text-sm font-normal text-gray-500">/100</span></span>
          </div>
          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${scoreBg} rounded-full transition-all duration-700`}
              style={{ width: `${report.score}%` }}
            />
          </div>
          <p className="text-xs text-gray-600 mt-2">{report.summary}</p>
          <p className="text-xs text-gray-400 mt-1">Scannet: {report.timestamp} · {report.entries.length} dataelementer fundet</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {(["findings", "raw"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-green-600 text-green-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "findings" ? `Fund (${report.findings.length})` : `Rådata (${report.entries.length})`}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {activeTab === "findings" && (
            <>
              {(["critical", "high", "medium", "low"] as RiskLevel[]).map((level) =>
                grouped[level].length > 0 && (
                  <div key={level}>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 mt-2 px-1">
                      {RISK_LABEL[level]} · {grouped[level].length}
                    </p>
                    {grouped[level].map((f, i) => (
                      <div key={i} className={`rounded-xl border p-3 mb-1.5 ${RISK_COLOR[level]}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="text-xs font-mono bg-black/5 px-1.5 py-0.5 rounded">{truncate(f.key, 40)}</code>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${RISK_BADGE[level]}`}>{f.source}</span>
                            </div>
                            <p className="text-xs mt-1 leading-relaxed">{f.reason}</p>
                            <p className="text-xs opacity-70 mt-0.5 italic">{f.gdprArticle}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}

          {activeTab === "raw" && (
            <>
              {report.entries.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Ingen data fundet i browser-lager.</p>
              ) : (
                report.entries.map((e, i) => (
                  <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs font-mono">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-400">[{e.source}]</span>
                      <span className="font-semibold text-gray-700">{truncate(e.key, 50)}</span>
                      <span className="text-gray-400 ml-auto">{e.sizeBytes} B</span>
                    </div>
                    <p className="text-gray-500 break-all">{truncate(e.value, 120)}</p>
                  </div>
                ))
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50">
          <a
            href="https://gdpr.eu/cookies/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-green-700 hover:underline"
          >
            GDPR cookie-regler ↗
          </a>
          <div className="flex gap-2">
            <button
              onClick={runAudit}
              className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Kør igen
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors"
              style={{ backgroundColor: "#2d6a4f" }}
            >
              Luk rapport
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
